import { useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../../lib/trpc';
import { Button } from '../../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  awaitingServer,
  refreshFailed,
  refreshUntilShown,
  shownState,
  unresolved,
} from '../../ui/QueryStates';
import { useTapOnce } from '../../ui/useTapOnce';
import { sendOnce } from '../../tickets/sendOnce';
import { IconSettings } from '../../icons';
import { evidenceLines, muteLine } from '../monitorsView';
import type { MuteRow } from './types';

/**
 * Muted (spec §7): every standing mute in the shop, with who made it, why and until when, and
 * Unmute. The owner lifts anyone's (`alert:configure`, which this list is read under); the
 * office lifts its own from the alert's panel. A failed read says so, never "nothing is muted".
 *
 * A lifted mute leaves the list, and its Unmute with it: the focus goes to the list's heading,
 * never to <body>.
 */
export function MutedList({ now }: { now: Date }) {
  const trpc = useTRPC();
  const q = useQuery(trpc.alert.mutes.queryOptions());
  const heading = useRef<HTMLHeadingElement>(null);
  const state = shownState(q, q.data);
  return (
    <section aria-labelledby="alerts-muted" className="mt-5" data-muted-list>
      <h3 id="alerts-muted" ref={heading} tabIndex={-1} className="text-sm font-semibold">
        Muted
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        A mute hides one subject’s alerts until a date. It is still checked, just not
        shown.
      </p>
      {!q.data ? (
        <div className="mt-2">
          <UnresolvedState
            state={unresolved(state)}
            what="the mutes"
            icon={IconSettings}
            error={q.error}
            onRetry={() => void q.refetch()}
            skeleton="h-12"
          />
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          {q.data.mutes.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-empty="muted">
              Nothing is muted.
            </p>
          ) : (
            <ul className="space-y-2">
              {q.data.mutes.map((m) => (
                <MutedItem
                  key={m.id}
                  mute={m}
                  tz={q.data.timeZone}
                  now={now}
                  onLifted={() => heading.current?.focus()}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export function MutedItem({
  mute,
  tz,
  now,
  onLifted,
}: {
  mute: MuteRow;
  tz: string;
  now: Date;
  onLifted: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const unmute = useMutation(
    trpc.alert.unmute.mutationOptions({
      onSuccess: () => {
        setError(null);
        return refreshUntilShown(qc, { queryKey: trpc.alert.mutes.queryKey() });
      },
      onError: (e) => setError(e.message),
      onSettled: () => lock.release(),
    }),
  );
  const subject = mute.subject
    ? evidenceLines(mute.subject.evidence, tz, now, mute.subject.type).headline
    : null;
  const busy = awaitingServer(unmute);
  return (
    <li
      className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-[var(--radius-sm)] border border-border p-2"
      data-mute={mute.id}
    >
      <div className="min-w-0 flex-[1_1_12rem]">
        <p id={titleId} className="text-sm font-medium break-words">
          {subject ? `${mute.label} · ${subject}` : mute.label}
        </p>
        <p className="mt-0.5 text-sm break-words text-muted-foreground">
          {muteLine(mute, tz, now)}
        </p>
        {error ? (
          <p role="alert" className="mt-1 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="min-h-9"
        aria-describedby={titleId}
        aria-disabled={busy || undefined}
        onClick={() => {
          if (busy) return;
          sendOnce(lock, (v) => unmute.mutate(v, { onSuccess: onLifted }), {
            muteId: mute.id,
          });
        }}
      >
        Unmute
      </Button>
    </li>
  );
}
